'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { AlertTriangle, ChevronRight, Shield, AlertCircle, AlertOctagon, Info, Loader2 } from 'lucide-react';

interface SeverityStats {
  severity: string;
  count: number;
  avgScore: number;
}

const SEVERITY_INFO: Record<
  string,
  { icon: React.ReactNode; description: string; color: string; bgColor: string; scoreRange: string }
> = {
  CRITICAL: {
    icon: <AlertOctagon className="w-6 h-6" />,
    description:
      'Highest severity vulnerabilities requiring immediate attention. Often allow remote code execution or complete system compromise.',
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    scoreRange: '9.0 - 10.0',
  },
  HIGH: {
    icon: <AlertTriangle className="w-6 h-6" />,
    description:
      'Serious vulnerabilities that can lead to significant data breaches or system damage. Should be patched promptly.',
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
    scoreRange: '7.0 - 8.9',
  },
  MEDIUM: {
    icon: <AlertCircle className="w-6 h-6" />,
    description:
      'Moderate severity vulnerabilities that may require user interaction or specific conditions to exploit.',
    color: 'text-yellow-600 dark:text-yellow-400',
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800',
    scoreRange: '4.0 - 6.9',
  },
  LOW: {
    icon: <Info className="w-6 h-6" />,
    description:
      'Lower impact vulnerabilities with limited exploitability. May require local access or other prerequisites.',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
    scoreRange: '0.1 - 3.9',
  },
};

export default function SeverityBrowseClient() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [severityStats, setSeverityStats] = useState<SeverityStats[]>([]);

  useEffect(() => {
    async function fetchSeverityStats() {
      try {
        const response = await fetch('/api/tools/cve/severity');
        if (!response.ok) {
          throw new Error('Failed to fetch severity data');
        }
        const data = await response.json();
        if (data.error) {
          throw new Error(data.error);
        }
        setSeverityStats(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    }
    fetchSeverityStats();
  }, []);

  const totalCVEs = severityStats.reduce((sum, s) => sum + s.count, 0);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading severity data...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-red-800 dark:text-red-200 mb-2">
          Unable to Load Data
        </h2>
        <p className="text-red-700 dark:text-red-300">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Overview */}
      <div className="bg-gradient-to-r from-red-600 to-orange-600 rounded-lg p-6 text-white">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-8 h-8" />
          <h2 className="text-2xl font-bold">Vulnerability Severity Overview</h2>
        </div>
        <p className="text-red-100 mb-4">
          CVEs are classified by CVSS severity to help prioritize remediation efforts. Critical and
          High severity vulnerabilities should be addressed immediately.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <div className="text-3xl font-bold">{totalCVEs.toLocaleString()}</div>
            <div className="text-sm text-red-200">Total CVEs with Severity</div>
          </div>
          <div>
            <div className="text-3xl font-bold">{severityStats.length}</div>
            <div className="text-sm text-red-200">Severity Levels</div>
          </div>
        </div>
      </div>

      {/* Severity Cards */}
      <div className="space-y-4">
        {severityStats.map((stat) => {
          const info = SEVERITY_INFO[stat.severity] || {
            icon: <AlertCircle className="w-6 h-6" />,
            description: 'CVE entries at this severity level.',
            color: 'text-gray-600 dark:text-gray-400',
            bgColor: 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700',
            scoreRange: 'N/A',
          };

          return (
            <Link
              key={stat.severity}
              href={`/tools/security/cve-lookup/severity/${stat.severity.toLowerCase()}`}
              className={`flex items-center justify-between p-6 border rounded-lg hover:shadow-lg transition-all group ${info.bgColor}`}
            >
              <div className="flex items-start gap-4">
                <div className={`flex-shrink-0 ${info.color}`}>{info.icon}</div>
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400">
                      {stat.severity} Severity
                    </h3>
                    <span className={`text-sm font-mono ${info.color}`}>
                      CVSS {info.scoreRange}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-xl">
                    {info.description}
                  </p>
                  <div className="flex items-center gap-4 mt-3 text-sm">
                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                      {stat.count.toLocaleString()} vulnerabilities
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">
                      Avg CVSS: {stat.avgScore.toFixed(1)}
                    </span>
                  </div>
                </div>
              </div>
              <ChevronRight className="w-6 h-6 text-gray-400 group-hover:text-blue-500 flex-shrink-0 transition-colors" />
            </Link>
          );
        })}
      </div>

      {severityStats.length === 0 && (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No CVE Data Available
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            CVE severity data is currently being loaded. Please try again later.
          </p>
        </div>
      )}

      {/* Severity Guide */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
          Prioritization Guide
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">
              Immediate Action Required
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <strong className="text-red-600 dark:text-red-400">Critical</strong> vulnerabilities
              should be patched within 24-48 hours.{' '}
              <strong className="text-orange-600 dark:text-orange-400">High</strong> severity issues
              within 1-2 weeks.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">Scheduled Patching</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <strong className="text-yellow-600 dark:text-yellow-400">Medium</strong> severity CVEs
              can be addressed in regular patch cycles.{' '}
              <strong className="text-green-600 dark:text-green-400">Low</strong> severity issues
              during maintenance windows.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
